import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, Award } from "lucide-react";
import { PHONE_TEL, WHATSAPP_URL, PHONE_DISPLAY, PORTRAIT_URL } from "../data/content";

const LINES = ["Interventional", "Pulmonologist,", "Critical Care &", "Sleep Medicine"];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden">
      <div className="breath-orb absolute -top-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-teal-500/10 blur-3xl pointer-events-none" />
      <div className="breath-orb absolute bottom-0 right-0 w-[28rem] h-[28rem] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" style={{ animationDelay: "3s" }} />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center relative z-10 w-full">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.8 }}
            className="font-mono-tag text-xs uppercase tracking-[0.3em] text-teal-700 mb-6 flex items-center gap-2">
            <span className="w-8 h-px bg-[#0B5A4E] inline-block" />
            Dr. Siddarth Singh · MBBS, MD, DM (AIIMS Bhubaneswar)
          </motion.p>

          <h1 data-testid="hero-heading"
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08] text-stone-800 tracking-tight"
            aria-label="Interventional Pulmonologist, Critical Care and Sleep Medicine specialist">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span className="block" custom={i} variants={lineVariants} initial="hidden" animate="show">
                  {i === 0 ? <span className="text-teal-700 italic">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 text-base sm:text-lg text-stone-500 max-w-xl leading-relaxed">
            Restoring every breath with clinical excellence — EBUS, bronchoscopy, thoracoscopy,
            ICU critical care and sleep medicine, now in Zirakpur & Panchkula.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <a href={PHONE_TEL} data-testid="hero-call-button"
              className="group flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#0B5A4E] text-[#FAF8F3] font-semibold text-sm hover:bg-[#0D6B5C] transition-colors duration-300">
              <Phone size={16} className="group-hover:rotate-12 transition-transform duration-300" />
              Call {PHONE_DISPLAY}
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button"
              className="flex items-center gap-3 px-7 py-3.5 rounded-full border border-[#25D366]/50 text-[#25D366] font-semibold text-sm hover:bg-[#25D366]/10 transition-colors duration-300">
              <MessageCircle size={16} /> WhatsApp for Appointment
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.35, duration: 1 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg" data-testid="hero-metrics">
            {[["10+", "Years of\nExperience"], ["5000+", "Bronchoscopy &\nEBUS Procedures"], ["2", "Hospitals in\nTricity"]].map(([num, label]) => (
              <div key={label}>
                <p className="font-serif-display text-3xl sm:text-4xl font-semibold text-teal-700">{num}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-500 whitespace-pre-line leading-snug">{label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: portraitY }}
          initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-sm" data-testid="hero-portrait-frame">
          <div className="gold-corner top-0 left-0 border-t-2 border-l-2 rounded-tl-lg" />
          <div className="gold-corner top-0 right-0 border-t-2 border-r-2 rounded-tr-lg" />
          <div className="gold-corner bottom-0 left-0 border-b-2 border-l-2 rounded-bl-lg" />
          <div className="gold-corner bottom-0 right-0 border-b-2 border-r-2 rounded-br-lg" />
          <div className="relative m-3 rounded-xl overflow-hidden border border-teal-400/30 shadow-[inset_0_0_40px_rgba(20,184,166,0.12),0_30px_80px_-20px_rgba(0,0,0,0.8)]">
            <img src={PORTRAIT_URL}
              alt="Dr. Siddarth Singh — Interventional Pulmonologist, Critical Care and Sleep Medicine specialist in Zirakpur and Panchkula"
              className="w-full aspect-[4/5] object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#FAF8F3]/80 via-transparent to-transparent" />
            <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF8F3]/80 backdrop-blur border border-amber-500/40" data-testid="hero-verified-badge">
              <Award size={12} className="text-amber-400" />
              <span className="font-mono-tag text-[10px] uppercase tracking-widest text-amber-700">DM · AIIMS</span>
            </div>
            <div className="absolute bottom-4 left-4 right-4">
              <p className="font-serif-display text-xl text-stone-800 font-semibold">Dr. Siddarth Singh</p>
              <p className="text-xs text-stone-500 mt-0.5">Consultant, Amcare Zirakpur · Director, IRCC Panchkula</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
